/** The Library's inspector pane: the selected asset's preview, state, actions and the fields the model flags. */
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { fieldsInModelOrder, isFieldVisible, missingNeededFields, videoFormatLabel, type AssetSummary, type MetadataSchemaDefinition } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useAssetAction, useMe } from '../../api/hooks';
import { bytes, duration, fps, relative, resolution } from '../../lib/format';
import { AssetThumb } from '../../components/SpriteThumb';
import { AssetStatus, Availability } from '../../components/Status';
import { toast } from '../../components/Toasts';
import { DeleteAssetButton } from '../../components/DeleteAsset';
import { AssetCode } from '../../components/AssetCode';
import { AddToCollectionButton } from '../../components/AddToCollection';
import { RestoreFromArchive } from '../../components/ArchiveRestore';
import { selectionSummary } from './useLibrarySelection';
import { AssetGroupsRow } from '../groups/AssetGroups';
import { RightsBanner } from '../../components/RightsMark';

export function LibraryInspector({ asset, schema }: { asset: AssetSummary; schema: MetadataSchemaDefinition | undefined }) {
  const navigate = useNavigate();
  const me = useMe().data!;
  const retry = useAssetAction('retry');
  const dismiss = useAssetAction('dismiss');
  const restore = useAssetAction('restore');
  const canRetry = me.permissions.includes('processing.retry');
  const tags = (asset.metadata.f_tags as string[] | undefined) ?? [];
  const description = asset.metadata.f_description as string | undefined;
  // The model can flag the fields the inspector shows (in model order); with none flagged, every field that has a value is shown.
  const flagged = schema ? fieldsInModelOrder(schema).filter((f) => f.inspector) : [];
  const categoryId = asset.category?.id ?? null;
  const rows = flagged.length
    ? flagged.filter((f) => isFieldVisible(f, asset.metadata, categoryId)).map((f) => ({ field: f, value: asset.metadata[f.id] }))
    : (schema?.fields ?? []).filter((f) => !['f_title', 'f_description', 'f_tags'].includes(f.id) && asset.metadata[f.id] != null).map((f) => ({ field: f, value: asset.metadata[f.id] }));
  const missing = asset.metadataIncomplete && schema ? missingNeededFields(schema, asset.metadata, categoryId) : [];
  return (
    <div className="inspector" data-testid="inspector">
      <div className="preview"><AssetThumb asset={asset} /></div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h2>{asset.title}</h2>
        <AssetCode code={asset.assetCode} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <AssetStatus status={asset.status} availability={asset.availability} />
          {/* 1.9.1: a ready asset's pill says where its original is; others still show it beside the status. */}
          {asset.status !== 'placeholder' && asset.status !== 'ready' && <Availability value={asset.availability} label={({ online: 'Original online', offline: 'Original offline', archived: 'Original archived', restoring: 'Restoring original…' } as Record<string, string>)[asset.availability]} />}
        </div>
      </div>
      <RestoreFromArchive assetId={asset.id} title={asset.title} availability={asset.availability} />
      <RightsBanner rights={asset.rights} />

      {asset.status === 'failed' && asset.error && (
        <div className="banner error" role="alert">
          <div className="banner-body">
            <strong>{asset.error.code === 'INVALID_MEDIA' ? 'Unreadable media' : 'Processing failed'}</strong>
            <span>{asset.error.message}</span>
            {asset.error.hint && <span className="muted">{asset.error.hint}</span>}
            {canRetry && (
              <div className="actions">
                <button className="btn sm" disabled={retry.isPending} onClick={() => retry.mutate(asset.id, { onSuccess: () => toast('Processing restarted'), onError: (e) => toast(errorMessage(e), 'error') })}><RotateCcw size={13} /> Retry</button>
                {!asset.dismissed && <button className="btn ghost sm" onClick={() => dismiss.mutate(asset.id, { onSuccess: () => toast('Dismissed from the library') })}>Dismiss</button>}
              </div>
            )}
          </div>
        </div>
      )}
      {asset.dismissed && canRetry && (
        <div className="banner warning"><div className="banner-body"><span>This asset is dismissed and hidden from the library.</span>
          <div className="actions"><button className="btn sm" onClick={() => restore.mutate(asset.id)}>Restore</button></div></div></div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" style={{ flex: 1 }} onClick={() => navigate(`/assets/${asset.id}`)} data-testid="open-asset">Open asset <kbd>↵</kbd></button>
        <AddToCollectionButton subject="asset" id={asset.id} name={asset.title} size="md" />
        <DeleteAssetButton asset={asset} size="md" />
      </div>
      {/* 1.4.0 (G2): the asset's groups, with Change and Remove. */}
      <AssetGroupsRow asset={asset} place="inspector" />
      {asset.metadataIncomplete && (
        <div className="banner warning" role="status" data-testid="inspector-incomplete"><div className="banner-body">
          <strong><AlertTriangle size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />Incomplete metadata</strong>
          <span>{missing.length ? `Needed and empty: ${missing.map((f) => f.label).join(', ')}.` : 'A needed field is empty.'}</span>
        </div></div>
      )}
      {!flagged.length && description && <p style={{ margin: 0, color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>{description}</p>}
      {!flagged.length && tags.length > 0 && <div className="tags">{tags.map((t) => <span className="tag" key={t}>{t}</span>)}</div>}
      {asset.status === 'placeholder' ? (
        <div>
          <p className="section-label">Media</p>
          <p className="muted" style={{ margin: 0 }} data-testid="inspector-no-media">No media yet. Open the asset to upload a file into it.</p>
        </div>
      ) : (
      <div>
        <p className="section-label">Media</p>
        <dl className="kv">
          <dt>Duration</dt><dd className="mono">{duration(asset.durationTicks)}</dd>
          <dt>Picture</dt><dd>{asset.mediaKind === 'audio' ? 'Audio only' : asset.mediaKind === 'graphic' ? `Graphic · ${resolution(asset.width, asset.height)}` : resolution(asset.width, asset.height)}</dd>
          <dt>Frame rate</dt><dd>{fps(asset.frameRate)}</dd>
          {asset.mediaKind === 'video' && asset.height ? <><dt>Format</dt><dd title={videoFormatLabel(asset).title ?? undefined} data-testid="inspector-format">{videoFormatLabel(asset).label}</dd></> : null}
          <dt>Original</dt><dd title={asset.originalFilename ?? undefined}>{asset.originalFilename}</dd>
          <dt>Size</dt><dd>{bytes(asset.sizeBytes)}</dd>
          <dt>Added</dt><dd>{relative(asset.createdAt)}{asset.createdBy ? ` by ${asset.createdBy}` : ''}</dd>
        </dl>
      </div>
      )}
      {schema && rows.length > 0 && (
        <div>
          <p className="section-label">Metadata</p>
          <dl className="kv" data-testid="inspector-fields">
            {rows.map(({ field: f, value }) => (
              <div key={f.id} style={{ display: 'contents' }} data-field={f.id}>
                <dt>{f.label}</dt>
                <dd>{inspectorValue(f, value)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

const KIND_LABELS: Record<string, [string, string]> = {
  video: ['video', 'videos'], audio: ['audio file', 'audio files'], image: ['image', 'images'], graphic: ['graphic', 'graphics'],
  placeholder: ['placeholder', 'placeholders'], other: ['other', 'others'],
};
const kindLabel = (k: string, n: number) => `${n} ${(KIND_LABELS[k] ?? [k, k])[n === 1 ? 0 : 1]}`;

/** The Inspector while several assets are selected (1.2.0): a summary instead of one asset. */
export function SelectionSummary({ assets, onClear }: { assets: AssetSummary[]; onClear: () => void }) {
  const s = selectionSummary(assets);
  const shown = assets.slice(0, 8);
  return (
    <div className="inspector selection-summary" data-testid="inspector-summary">
      <h2 data-testid="inspector-summary-count">{s.count} assets selected</h2>
      <dl className="kv">
        <dt>Kinds</dt><dd data-testid="inspector-summary-kinds">{s.kinds.map(([k, n]) => kindLabel(k, n)).join(' · ')}</dd>
        <dt>Total size</dt><dd data-testid="inspector-summary-size">{bytes(s.sizeBytes)}</dd>
        {s.durationTicks > 0 && <><dt>Total length</dt><dd className="mono">{duration(s.durationTicks)}</dd></>}
        {s.archived > 0 && <><dt>Archived</dt><dd>{s.archived}</dd></>}
      </dl>
      <div>
        <p className="section-label">Selected</p>
        <ul className="selection-summary-list">
          {shown.map((a) => <li key={a.id} title={a.title}>{a.title}</li>)}
          {assets.length > shown.length && <li className="faint">and {assets.length - shown.length} more</li>}
        </ul>
      </div>
      <button className="btn" onClick={onClear} data-testid="inspector-summary-clear">Clear selection <kbd>Esc</kbd></button>
    </div>
  );
}

/** A field value as the inspector shows it; flagged fields without a value show a dash. */
function inspectorValue(f: MetadataSchemaDefinition['fields'][number], value: unknown): string {
  if (value == null || value === '' || (Array.isArray(value) && !value.length)) return '—';
  if (f.type === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map((v) => f.options?.find((o) => o.value === v)?.label ?? String(v)).join(', ');
  return f.options ? f.options.find((o) => o.value === value)?.label ?? String(value) : String(value);
}
